// =====================================================
// GRAPHQL OPERATIONS
// All GraphQL queries, mutations, and subscriptions
// =====================================================

import { gql } from '@apollo/client';

// =====================================================
// FRAGMENTS
// =====================================================

export const FEATURE_FRAGMENT = gql`
  fragment FeatureFields on Feature {
    id
    tenantId
    name
    description
    featureType
    config
    status
    version
    parentFeatureId
    createdBy
    createdAt
    updatedAt
  }
`;

export const AGENT_FRAGMENT = gql`
  fragment AgentFields on Agent {
    id
    tenantId
    name
    type
    status
    config
    capabilities
    createdAt
    updatedAt
  }
`;

export const USER_FRAGMENT = gql`
  fragment UserFields on User {
    id
    tenantId
    email
    name
    role
    permissions
    avatar
    lastLoginAt
    createdAt
    updatedAt
  }
`;

export const DEPLOYMENT_FRAGMENT = gql`
  fragment DeploymentFields on Deployment {
    id
    featureId
    status
    environment
    version
    previewUrl
    productionUrl
    deployedBy
    createdAt
    updatedAt
    completedAt
  }
`;

export const EXECUTION_FRAGMENT = gql`
  fragment ExecutionFields on FeatureExecution {
    id
    featureId
    tenantId
    executionType
    status
    duration
    errorMessage
    executedBy
    executedAt
    completedAt
    createdAt
    updatedAt
  }
`;

// =====================================================
// AUTHENTICATION QUERIES & MUTATIONS
// =====================================================

export const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      user {
        ...UserFields
      }
      accessToken
      refreshToken
      expiresIn
    }
  }
  ${USER_FRAGMENT}
`;

export const REGISTER_MUTATION = gql`
  mutation Register($input: CreateUserInput!) {
    register(input: $input) {
      user {
        ...UserFields
      }
      accessToken
      refreshToken
      expiresIn
    }
  }
  ${USER_FRAGMENT}
`;

export const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      accessToken
      expiresIn
    }
  }
`;

export const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout
  }
`;

export const ME_QUERY = gql`
  query Me {
    me {
      ...UserFields
    }
  }
  ${USER_FRAGMENT}
`;

// =====================================================
// TENANT QUERIES & MUTATIONS
// =====================================================

export const GET_CURRENT_TENANT = gql`
  query GetCurrentTenant {
    currentTenant {
      id
      name
      slug
      domain
      planType
      status
      settings
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_TENANT_MUTATION = gql`
  mutation CreateTenant($input: CreateTenantInput!) {
    createTenant(input: $input) {
      id
      name
      slug
      domain
      planType
      status
      createdAt
    }
  }
`;

export const UPDATE_TENANT_MUTATION = gql`
  mutation UpdateTenant($id: ID!, $input: UpdateTenantInput!) {
    updateTenant(id: $id, input: $input) {
      id
      name
      domain
      planType
      status
      settings
      updatedAt
    }
  }
`;

// =====================================================
// FEATURE QUERIES & MUTATIONS
// =====================================================

export const GET_FEATURES_QUERY = gql`
  query GetFeatures($tenantId: ID!, $input: SearchFeaturesInput) {
    features(tenantId: $tenantId, input: $input) {
      items {
        ...FeatureFields
        agents {
          ...AgentFields
        }
      }
      total
      page
      limit
      totalPages
      hasNext
      hasPrev
    }
  }
  ${FEATURE_FRAGMENT}
  ${AGENT_FRAGMENT}
`;

export const GET_FEATURE_QUERY = gql`
  query GetFeature($id: ID!) {
    feature(id: $id) {
      ...FeatureFields
      parent {
        id
        name
      }
      children {
        id
        name
        featureType
      }
      agents {
        ...AgentFields
        metrics {
          executionCount
          successRate
          averageExecutionTime
          errorCount
          lastExecutionAt
        }
      }
      dependencies {
        id
        dependsOnId
        type
        dependsOn {
          id
          name
        }
      }
      executions(limit: 10) {
        ...ExecutionFields
      }
      deployments(limit: 5) {
        ...DeploymentFields
      }
      creator {
        ...UserFields
      }
    }
  }
  ${FEATURE_FRAGMENT}
  ${AGENT_FRAGMENT}
  ${USER_FRAGMENT}
  ${EXECUTION_FRAGMENT}
  ${DEPLOYMENT_FRAGMENT}
`;

export const CREATE_FEATURE_MUTATION = gql`
  mutation CreateFeature($tenantId: ID!, $input: CreateFeatureInput!) {
    createFeature(tenantId: $tenantId, input: $input) {
      ...FeatureFields
    }
  }
  ${FEATURE_FRAGMENT}
`;

export const UPDATE_FEATURE_MUTATION = gql`
  mutation UpdateFeature($id: ID!, $input: UpdateFeatureInput!) {
    updateFeature(id: $id, input: $input) {
      ...FeatureFields
    }
  }
  ${FEATURE_FRAGMENT}
`;

export const DELETE_FEATURE_MUTATION = gql`
  mutation DeleteFeature($id: ID!) {
    deleteFeature(id: $id)
  }
`;

export const DEPLOY_FEATURE_MUTATION = gql`
  mutation DeployFeature($input: DeployFeatureInput!) {
    deployFeature(input: $input) {
      deployment {
        ...DeploymentFields
      }
      agents {
        ...AgentFields
      }
      previewUrl
      estimatedTime
    }
  }
  ${DEPLOYMENT_FRAGMENT}
  ${AGENT_FRAGMENT}
`;

export const SEARCH_FEATURES_QUERY = gql`
  query SearchFeatures($query: String!, $tenantId: ID!) {
    searchFeatures(query: $query, tenantId: $tenantId) {
      ...FeatureFields
    }
  }
  ${FEATURE_FRAGMENT}
`;

// =====================================================
// AGENT QUERIES & MUTATIONS
// =====================================================

export const GET_AGENTS_QUERY = gql`
  query GetAgents($tenantId: ID!, $type: AgentType) {
    agents(tenantId: $tenantId, type: $type) {
      ...AgentFields
      features {
        id
        name
      }
      metrics {
        executionCount
        successRate
        averageExecutionTime
        errorCount
        lastExecutionAt
      }
    }
  }
  ${AGENT_FRAGMENT}
`;

export const GET_AGENT_QUERY = gql`
  query GetAgent($id: ID!) {
    agent(id: $id) {
      ...AgentFields
      features {
        ...FeatureFields
      }
      metrics {
        executionCount
        successRate
        averageExecutionTime
        errorCount
        lastExecutionAt
      }
    }
  }
  ${AGENT_FRAGMENT}
  ${FEATURE_FRAGMENT}
`;

export const CREATE_AGENT_MUTATION = gql`
  mutation CreateAgent($tenantId: ID!, $input: CreateAgentInput!) {
    createAgent(tenantId: $tenantId, input: $input) {
      ...AgentFields
    }
  }
  ${AGENT_FRAGMENT}
`;

export const UPDATE_AGENT_MUTATION = gql`
  mutation UpdateAgent($id: ID!, $config: JSON!) {
    updateAgent(id: $id, config: $config) {
      ...AgentFields
    }
  }
  ${AGENT_FRAGMENT}
`;

export const DELETE_AGENT_MUTATION = gql`
  mutation DeleteAgent($id: ID!) {
    deleteAgent(id: $id)
  }
`;

export const RESTART_AGENT_MUTATION = gql`
  mutation RestartAgent($id: ID!) {
    restartAgent(id: $id) {
      ...AgentFields
    }
  }
  ${AGENT_FRAGMENT}
`;

// =====================================================
// EXECUTION QUERIES & MUTATIONS
// =====================================================

export const GET_EXECUTIONS_QUERY = gql`
  query GetExecutions(
    $featureId: ID
    $tenantId: ID!
    $status: ExecutionStatus
    $pagination: PaginationInput
  ) {
    executions(
      featureId: $featureId
      tenantId: $tenantId
      status: $status
      pagination: $pagination
    ) {
      items {
        ...ExecutionFields
        feature {
          id
          name
        }
        user {
          id
          name
          email
        }
      }
      total
      page
      limit
      totalPages
      hasNext
      hasPrev
    }
  }
  ${EXECUTION_FRAGMENT}
`;

export const EXECUTE_FEATURE_MUTATION = gql`
  mutation ExecuteFeature($featureId: ID!, $inputData: JSON) {
    executeFeature(featureId: $featureId, inputData: $inputData) {
      ...ExecutionFields
    }
  }
  ${EXECUTION_FRAGMENT}
`;

export const CANCEL_EXECUTION_MUTATION = gql`
  mutation CancelExecution($id: ID!) {
    cancelExecution(id: $id)
  }
`;

// =====================================================
// DEPLOYMENT QUERIES
// =====================================================

export const GET_DEPLOYMENTS_QUERY = gql`
  query GetDeployments($featureId: ID!, $environment: String) {
    deployments(featureId: $featureId, environment: $environment) {
      ...DeploymentFields
      feature {
        id
        name
      }
      deployer {
        id
        name
        email
      }
      logs {
        timestamp
        level
        message
        metadata
      }
    }
  }
  ${DEPLOYMENT_FRAGMENT}
`;

export const ROLLBACK_DEPLOYMENT_MUTATION = gql`
  mutation RollbackDeployment($deploymentId: ID!) {
    rollbackDeployment(deploymentId: $deploymentId) {
      ...DeploymentFields
    }
  }
  ${DEPLOYMENT_FRAGMENT}
`;

// =====================================================
// ANALYTICS QUERIES
// =====================================================

export const GET_ANALYTICS_QUERY = gql`
  query GetAnalytics($featureId: ID, $tenantId: ID!, $timeframe: String!) {
    analytics(featureId: $featureId, tenantId: $tenantId, timeframe: $timeframe) {
      featureId
      tenantId
      timeframe
      metrics
      trends
      insights
    }
  }
`;

export const GET_USAGE_METRICS_QUERY = gql`
  query GetUsageMetrics($tenantId: ID!, $period: String!) {
    usageMetrics(tenantId: $tenantId, period: $period) {
      featureExecutions
      apiCalls
      storageUsed
      activeUsers
      period
      timestamp
    }
  }
`;

// =====================================================
// SYSTEM QUERIES
// =====================================================

export const GET_SYSTEM_HEALTH_QUERY = gql`
  query GetSystemHealth {
    systemHealth {
      status
      uptime
      services {
        name
        status
        latency
        errorRate
      }
      metrics {
        activeAgents
        queuedExecutions
        averageResponseTime
        requestsPerSecond
      }
      timestamp
    }
  }
`;

// =====================================================
// SUBSCRIPTIONS
// =====================================================

export const FEATURE_UPDATED_SUBSCRIPTION = gql`
  subscription FeatureUpdated($tenantId: ID!, $featureId: ID) {
    featureUpdated(tenantId: $tenantId, featureId: $featureId) {
      feature {
        ...FeatureFields
      }
      changeType
      changes
      updatedBy {
        ...UserFields
      }
      timestamp
    }
  }
  ${FEATURE_FRAGMENT}
  ${USER_FRAGMENT}
`;

export const FEATURE_CREATED_SUBSCRIPTION = gql`
  subscription FeatureCreated($tenantId: ID!) {
    featureCreated(tenantId: $tenantId) {
      ...FeatureFields
    }
  }
  ${FEATURE_FRAGMENT}
`;

export const FEATURE_DELETED_SUBSCRIPTION = gql`
  subscription FeatureDeleted($tenantId: ID!) {
    featureDeleted(tenantId: $tenantId)
  }
`;

export const AGENT_STATUS_CHANGED_SUBSCRIPTION = gql`
  subscription AgentStatusChanged($tenantId: ID!, $agentId: ID) {
    agentStatusChanged(tenantId: $tenantId, agentId: $agentId) {
      agent {
        ...AgentFields
      }
      previousStatus
      newStatus
      reason
      timestamp
    }
  }
  ${AGENT_FRAGMENT}
`;

export const DEPLOYMENT_PROGRESS_SUBSCRIPTION = gql`
  subscription DeploymentProgress($deploymentId: ID!) {
    deploymentProgress(deploymentId: $deploymentId) {
      deployment {
        ...DeploymentFields
      }
      status
      progress
      logs {
        timestamp
        level
        message
        metadata
      }
      timestamp
    }
  }
  ${DEPLOYMENT_FRAGMENT}
`;

export const EXECUTION_UPDATED_SUBSCRIPTION = gql`
  subscription ExecutionUpdated($featureId: ID!) {
    executionUpdated(featureId: $featureId) {
      ...ExecutionFields
    }
  }
  ${EXECUTION_FRAGMENT}
`;

export const COLLABORATION_EVENT_SUBSCRIPTION = gql`
  subscription CollaborationEvent($featureId: ID!) {
    collaborationEvent(featureId: $featureId) {
      type
      featureId
      userId
      user {
        ...UserFields
      }
      data
      timestamp
    }
  }
  ${USER_FRAGMENT}
`;

export const CURSOR_MOVED_SUBSCRIPTION = gql`
  subscription CursorMoved($featureId: ID!) {
    cursorMoved(featureId: $featureId) {
      userId
      user {
        id
        name
        email
        avatar
      }
      featureId
      position
      timestamp
    }
  }
`;

export const NOTIFICATION_SUBSCRIPTION = gql`
  subscription Notification($userId: ID!) {
    notification(userId: $userId) {
      id
      type
      title
      message
      data
      read
      userId
      createdAt
    }
  }
`;
